/**
 * Shows that have drifted out of the rotation.
 *
 * TV Time called this "haven't watched for a while", and the point is that a
 * show you stopped three months ago should not sit at the top of the queue
 * competing with the one you are actually watching — but deleting it is far
 * too strong, because you might go back.
 *
 * Two ways in. The reliable one is manual: you say so. The automatic one is
 * time, and it comes with a caveat worth stating plainly — Simkl's
 * `last_watched_at` records when an episode was *marked*, not when it was
 * watched. Marking a back catalogue in one sitting therefore makes every show
 * look freshly watched. Measured on a real library right after an import,
 * nothing at all was older than thirty days. So the clock only tells the truth
 * about shows marked as they are watched, and it earns its keep over months
 * rather than today.
 */
import { remaining } from "@/lib/episode";
import type { LibraryShow } from "@/api/schemas";
import type { QueueEntry } from "@/lib/episode";

/** Past this with no activity, a show has drifted rather than paused. */
export const STALE_DAYS = 60;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * When this show was last touched: watched if ever, else added.
 *
 * A show added a year ago and never started has drifted just as surely as one
 * abandoned halfway, and `last_watched_at` is null for it.
 */
export function lastActivity(show: LibraryShow): number | null {
  const stamp = show.lastWatchedAt ?? show.addedAt;
  if (stamp === null) return null;
  const parsed = Date.parse(stamp);
  return Number.isNaN(parsed) ? null : parsed;
}

export function daysSince(show: LibraryShow, now: number): number | null {
  const at = lastActivity(show);
  return at === null ? null : Math.floor((now - at) / DAY_MS);
}

/** Set aside by hand, or quiet for long enough to count as set aside. */
export function isStale(
  show: LibraryShow,
  asideIds: ReadonlySet<number>,
  now: number = Date.now(),
): boolean {
  if (asideIds.has(show.show.ids.simkl)) return true;
  if (remaining(show) === 0) return false;
  const days = daysSince(show, now);
  return days !== null && days >= STALE_DAYS;
}

export interface SplitQueue {
  active: QueueEntry[];
  stale: QueueEntry[];
}

/** The queue in two parts, each keeping the order it already had. */
export function splitQueue(
  queue: readonly QueueEntry[],
  asideIds: ReadonlySet<number>,
  now: number = Date.now(),
): SplitQueue {
  const active: QueueEntry[] = [];
  const stale: QueueEntry[] = [];
  for (const entry of queue) {
    (isStale(entry.show, asideIds, now) ? stale : active).push(entry);
  }
  return { active, stale };
}
